// src/utils/visorApp.js
// ═══════════════════════════════════════════════════════════════════════════
// Visor de documentos para la app instalada en el teléfono.
//
// El problema: el portal abre sus PDF y documentos de dos formas pensadas para
// una computadora — una ventana nueva del navegador (window.open) o una
// descarga (<a download href="blob:…">). Instalado como app (pantalla completa,
// sin barra del navegador) el teléfono no tiene dónde abrir esa ventana ni
// adónde mandar esa descarga: el toque no hace nada o deja la pantalla en
// blanco sin forma de regresar.
//
// La solución: SOLO cuando el portal corre como app en un teléfono, esas dos
// salidas se redirigen a un visor dentro de la misma app (capa a pantalla
// completa con «Cerrar» y «Compartir»). Los generadores no se tocan
// (generarPDFResultado.js sigue bloqueado): se atrapa lo que producen.
//   · window.open('')        → la «ventana» es un marco dentro del visor; todo
//                              lo que el generador escribe y sus botones
//                              (Descargar, Imprimir, Correo, WhatsApp) siguen
//                              funcionando ahí.
//   · window.open(blob:…)    → se muestra el documento en el visor.
//   · <a download blob:…>    → se muestra el archivo en el visor.
//   · enlaces http(s)        → no se tocan (los abre el sistema).
// En computadora —y en el navegador normal del teléfono— nada de esto se
// instala: el comportamiento sigue idéntico.
// ═══════════════════════════════════════════════════════════════════════════

import { abrirCapa, soltarCapa } from './capas';

const PDFJS_VER = '3.11.174';
const PDFJS_URL = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VER}/pdf.min.js`;
const PDFJS_WORKER = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VER}/pdf.worker.min.js`;
const AZUL = '#152643';

// ── ¿Corre como app instalada en un teléfono o tableta? ─────────────────────
export function esAppInstalada() {
  try {
    const standalone =
      (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
      (window.matchMedia && window.matchMedia('(display-mode: fullscreen)').matches) ||
      window.navigator.standalone === true;
    if (!standalone) return false;
    const tactil =
      /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || '') ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||   // iPad moderno
      (window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
    return !!tactil;
  } catch {
    return false;
  }
}

// ── Registro de Blobs: el generador revoca el enlace justo después del clic ──
const blobs = new Map();   // url → Blob

function nombreDesdeTipo(tipo) {
  if (/pdf/i.test(tipo)) return 'documento.pdf';
  if (/sheet|excel/i.test(tipo)) return 'archivo.xlsx';
  if (/csv/i.test(tipo)) return 'archivo.csv';
  return 'archivo';
}

// ── Capa del visor ──────────────────────────────────────────────────────────
function crearCapa(titulo) {
  const capa = document.createElement('div');
  capa.setAttribute('role', 'dialog');
  capa.setAttribute('aria-modal', 'true');
  capa.style.cssText =
    'position:fixed;inset:0;z-index:2147483000;background:#f1f5f9;display:flex;flex-direction:column;' +
    'font-family:Arial,sans-serif';

  const barra = document.createElement('div');
  barra.style.cssText =
    `flex-shrink:0;background:${AZUL};color:#fff;display:flex;align-items:center;gap:8px;` +
    'padding:8px 10px;padding-top:calc(8px + env(safe-area-inset-top));min-height:52px;box-sizing:border-box';

  const cerrar = document.createElement('button');
  cerrar.type = 'button';
  cerrar.textContent = '‹ Cerrar';
  cerrar.style.cssText = estiloBoton(true);

  const tit = document.createElement('div');
  tit.textContent = titulo || 'Documento';
  tit.style.cssText = 'flex:1;min-width:0;font-size:14px;font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis';

  const acciones = document.createElement('div');
  acciones.style.cssText = 'display:flex;gap:6px;flex-shrink:0';

  barra.append(cerrar, tit, acciones);

  const cuerpo = document.createElement('div');
  cuerpo.style.cssText =
    'flex:1;min-height:0;position:relative;overflow:auto;-webkit-overflow-scrolling:touch;' +
    'padding-bottom:env(safe-area-inset-bottom)';

  capa.append(barra, cuerpo);
  document.body.appendChild(capa);

  const prevOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';

  let alCerrar = [];
  let idCapa = null;
  const ctl = {
    capa, cuerpo, tit, acciones, cerrada: false,
    onCerrar(fn) { alCerrar.push(fn); },
    cerrar() {
      if (ctl.cerrada) return;
      ctl.cerrada = true;
      alCerrar.forEach((fn) => { try { fn(); } catch { /* nada */ } });
      alCerrar = [];
      capa.remove();
      // Cerrado con «Cerrar» o por el documento: se quita su entrada del
      // historial. Si lo cerró "atrás", la entrada ya no está y esto no hace nada.
      if (idCapa) { soltarCapa(idCapa); idCapa = null; }
      if (!document.querySelector('[data-visor-app]')) document.body.style.overflow = prevOverflow;
    },
  };
  capa.setAttribute('data-visor-app', '1');
  cerrar.onclick = () => ctl.cerrar();
  // "Atrás" del teléfono cierra el visor en vez de salir de la app.
  idCapa = abrirCapa(() => { idCapa = null; ctl.cerrar(); });
  return ctl;
}

function estiloBoton(transparente) {
  return 'height:36px;padding:0 12px;border-radius:8px;font-size:13px;font-weight:700;cursor:pointer;' +
    'font-family:inherit;white-space:nowrap;' +
    (transparente
      ? 'background:transparent;color:#fff;border:1px solid rgba(255,255,255,.35)'
      : 'background:#fff;color:' + AZUL + ';border:none');
}

function botonCompartir(ctl, blob, nombre) {
  let archivo;
  try { archivo = new File([blob], nombre, { type: blob.type || 'application/octet-stream' }); }
  catch { return; }
  let puede = false;
  try { puede = !!(navigator.canShare && navigator.share && navigator.canShare({ files: [archivo] })); }
  catch { puede = false; }
  if (!puede) return;
  const b = document.createElement('button');
  b.type = 'button';
  b.textContent = 'Compartir';
  b.style.cssText = estiloBoton(false);
  b.onclick = () => { navigator.share({ files: [archivo], title: nombre }).catch(() => {}); };
  ctl.acciones.appendChild(b);
}

function mensaje(ctl, texto) {
  const m = document.createElement('div');
  m.style.cssText = 'padding:40px 20px;text-align:center;color:#0d2b45;font-size:14px;line-height:1.5';
  m.textContent = texto;
  ctl.cuerpo.appendChild(m);
  return m;
}

// ── pdf.js bajo demanda (desde cdnjs, solo la primera vez que se ve un PDF) ──
let cargaPdfjs = null;
function pdfjs() {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (!cargaPdfjs) {
    cargaPdfjs = new Promise((ok, mal) => {
      const s = document.createElement('script');
      s.src = PDFJS_URL;
      s.async = true;
      s.onload = () => {
        if (!window.pdfjsLib) { mal(new Error('pdf.js no cargó')); return; }
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
        ok(window.pdfjsLib);
      };
      s.onerror = () => { cargaPdfjs = null; mal(new Error('Sin conexión al visor')); };
      document.head.appendChild(s);
    });
  }
  return cargaPdfjs;
}

async function pintarPDF(ctl, blob, nombre) {
  const aviso = mensaje(ctl, 'Abriendo documento…');
  try {
    const lib = await pdfjs();
    const datos = new Uint8Array(await blob.arrayBuffer());
    const doc = await lib.getDocument({ data: datos }).promise;
    if (ctl.cerrada) return;
    aviso.remove();
    const cont = document.createElement('div');
    cont.style.cssText = 'padding:10px 8px 24px;display:flex;flex-direction:column;align-items:center;gap:10px';
    ctl.cuerpo.appendChild(cont);
    const ancho = Math.max(280, ctl.cuerpo.clientWidth - 16);
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    for (let n = 1; n <= doc.numPages; n++) {
      if (ctl.cerrada) return;
      const pag = await doc.getPage(n);
      const base = pag.getViewport({ scale: 1 });
      const escala = ancho / base.width;
      const vp = pag.getViewport({ scale: escala * dpr });
      const canvas = document.createElement('canvas');
      canvas.width = Math.floor(vp.width);
      canvas.height = Math.floor(vp.height);
      canvas.style.cssText =
        `width:${Math.floor(base.width * escala)}px;height:auto;background:#fff;` +
        'box-shadow:0 1px 4px rgba(15,23,42,.18);border-radius:2px;max-width:100%';
      cont.appendChild(canvas);
      await pag.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
    }
  } catch (e) {
    aviso.textContent =
      'No se pudo mostrar el documento aquí (' + (e?.message || e) + '). ' +
      'Usa «Compartir» para abrirlo, guardarlo o enviarlo.';
    // Respaldo: abrirlo en el navegador del teléfono.
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.textContent = 'Abrir en el navegador';
    a.href = url; a.target = '_blank'; a.rel = 'noopener';
    a.setAttribute('data-visor-libre', '1');
    a.style.cssText = 'display:inline-block;margin-top:14px;color:' + AZUL + ';font-weight:700';
    aviso.appendChild(document.createElement('br'));
    aviso.appendChild(a);
    ctl.onCerrar(() => URL.revokeObjectURL(url));
  }
  void nombre;
}

// ── Mostrar un archivo (Blob) en el visor ───────────────────────────────────
export function mostrarArchivo(blob, nombre) {
  const tipo = (blob && blob.type) || '';
  nombre = nombre || nombreDesdeTipo(tipo);
  const ctl = crearCapa(nombre);
  if (/pdf/i.test(tipo) || /\.pdf$/i.test(nombre)) {
    botonCompartir(ctl, blob, nombre);
    pintarPDF(ctl, blob, nombre);
  } else if (/html/i.test(tipo)) {
    const url = URL.createObjectURL(blob);
    const marco = crearMarco(ctl, url);
    prepararMarco(marco, ctl);
    ctl.onCerrar(() => URL.revokeObjectURL(url));
  } else {
    botonCompartir(ctl, blob, nombre);
    mensaje(ctl, 'El archivo «' + nombre + '» está listo. Usa «Compartir» para guardarlo en Archivos o enviarlo.');
  }
  return ctl;
}

// ── Marco que hace de «ventana nueva» ───────────────────────────────────────
function crearMarco(ctl, src) {
  const marco = document.createElement('iframe');
  marco.title = 'Documento';
  marco.style.cssText = 'border:0;width:100%;height:100%;display:block;background:#fff';
  if (src) marco.src = src;
  ctl.cuerpo.style.overflow = 'hidden';
  ctl.cuerpo.appendChild(marco);
  return marco;
}

// Dentro del marco, lo que el documento hace como «ventana» se redirige:
// abrir otra ventana, descargar, cerrarse. Se reaplica porque escribir el
// documento (document.write) o navegar puede restablecer su entorno.
function prepararMarco(marco, ctl) {
  const aplicar = () => {
    let w;
    try { w = marco.contentWindow; } catch { return; }
    if (!w || ctl.cerrada) return;
    try {
      if (!w.__visorApp) {
        w.__visorApp = true;
        w.open = (...args) => window.open(...args);
        w.close = () => ctl.cerrar();
        const Proto = w.HTMLAnchorElement && w.HTMLAnchorElement.prototype;
        if (Proto) {
          const clickOriginal = Proto.click;
          Proto.click = function (...args) {
            if (atraparEnlace(this)) return undefined;
            return clickOriginal.apply(this, args);
          };
        }
        w.document.addEventListener('click', (e) => {
          const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
          if (a && atraparEnlace(a)) e.preventDefault();
        }, true);
      }
      const t = w.document && w.document.title;
      if (t) ctl.tit.textContent = t;
    } catch { /* otro origen: nada que preparar */ }
  };
  aplicar();
  marco.addEventListener('load', aplicar);
  [0, 50, 300, 1000].forEach((ms) => setTimeout(aplicar, ms));
  return aplicar;
}

function ventanaEnVisor(titulo) {
  const ctl = crearCapa(titulo || 'Documento');
  const marco = crearMarco(ctl, '');
  const w = marco.contentWindow;
  // Que el documento sepa quién lo abrió (algunos generadores usan opener).
  try { w.opener = window; } catch { /* nada */ }
  const aplicar = prepararMarco(marco, ctl);
  // document.write puede reemplazar el documento: se vuelve a preparar.
  try {
    const d = w.document;
    const escribir = d.write.bind(d);
    d.write = (...args) => { escribir(...args); setTimeout(aplicar, 0); };
    const cerrarDoc = d.close.bind(d);
    d.close = () => { cerrarDoc(); aplicar(); };
  } catch { /* nada */ }
  return w;
}

// ── Enlace a un Blob: ¿lo mostramos en el visor? ────────────────────────────
function atraparEnlace(a) {
  if (!a || a.hasAttribute('data-visor-libre')) return false;
  const href = a.href || '';
  if (!href.startsWith('blob:')) return false;
  const descarga = a.hasAttribute('download');
  const nueva = (a.getAttribute('target') || '') === '_blank';
  if (!descarga && !nueva) return false;
  const blob = blobs.get(href);
  if (!blob) return false;
  mostrarArchivo(blob, a.getAttribute('download') || '');
  return true;
}

// ── Instalación (una sola vez, al arrancar) ─────────────────────────────────
let instalado = false;
export function instalarVisorApp() {
  if (instalado || typeof window === 'undefined' || !esAppInstalada()) return;
  instalado = true;

  const crearOriginal = URL.createObjectURL.bind(URL);
  const revocarOriginal = URL.revokeObjectURL.bind(URL);
  URL.createObjectURL = function (obj) {
    const url = crearOriginal(obj);
    if (obj instanceof Blob) blobs.set(url, obj);
    return url;
  };
  // El generador revoca justo después del clic: se pospone para que el visor
  // alcance a leer el archivo.
  URL.revokeObjectURL = function (url) {
    if (blobs.has(url)) {
      setTimeout(() => { blobs.delete(url); try { revocarOriginal(url); } catch { /* nada */ } }, 120000);
      return undefined;
    }
    return revocarOriginal(url);
  };

  const abrirOriginal = window.open.bind(window);
  window.open = function (url, destino, opciones) {
    const u = url == null ? '' : String(url);
    if (u === '' || u === 'about:blank') return ventanaEnVisor('');
    if (u.startsWith('blob:')) {
      const blob = blobs.get(u);
      if (blob) {
        const ctl = mostrarArchivo(blob, '');
        return { closed: false, close: () => ctl.cerrar(), focus() {}, document: null };
      }
    }
    return abrirOriginal(url, destino, opciones);
  };

  const clickOriginal = HTMLAnchorElement.prototype.click;
  HTMLAnchorElement.prototype.click = function (...args) {
    if (atraparEnlace(this)) return undefined;
    return clickOriginal.apply(this, args);
  };
  document.addEventListener('click', (e) => {
    const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (a && atraparEnlace(a)) e.preventDefault();
  }, true);
}
